import * as React from 'react'
import { CreditCard, Receipt, TrendingUp, Plus } from 'lucide-react'

import type { Invoice } from '@/gen/cortado/v1/invoices_pb'
import { InvoiceStatusEnum } from '@/gen/cortado/v1/settings_pb'
import { formatCurrency } from '@/lib/currency-utils'
import { getCustomerFacingPaymentTotals } from '@/lib/invoice-payment-totals'
import { cn } from '@/lib/utils'

import { ActionGate } from '@/components/shared/action-gate'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'

interface PaymentOverviewCardProps {
  invoice: Invoice
  paymentCount: number
  onRecordPayment: () => void
}

/**
 * Get invoice payment status details for display
 */
function getPaymentStatusDisplay(invoice: Invoice): {
  label: string
  variant: 'default' | 'success' | 'warning' | 'danger' | 'secondary'
  description: string
} {
  const status = invoice.status

  switch (status) {
    case InvoiceStatusEnum.INVOICE_STATUS_PAID:
      return {
        label: 'Paid in Full',
        variant: 'success',
        description: 'All payments received',
      }
    case InvoiceStatusEnum.INVOICE_STATUS_PARTIALLY_PAID:
      return {
        label: 'Partially Paid',
        variant: 'warning',
        description: 'Payment in progress',
      }
    case InvoiceStatusEnum.INVOICE_STATUS_OVERDUE:
      return {
        label: 'Overdue',
        variant: 'danger',
        description: 'Payment past due',
      }
    case InvoiceStatusEnum.INVOICE_STATUS_SENT:
      return {
        label: 'Awaiting Payment',
        variant: 'secondary',
        description: 'Invoice sent to customer',
      }
    case InvoiceStatusEnum.INVOICE_STATUS_DRAFT:
      return {
        label: 'Draft',
        variant: 'secondary',
        description: 'Not yet sent',
      }
    case InvoiceStatusEnum.INVOICE_STATUS_CANCELLED:
      return {
        label: 'Cancelled',
        variant: 'danger',
        description: 'Invoice cancelled',
      }
    default:
      return {
        label: 'Unknown',
        variant: 'secondary',
        description: '',
      }
  }
}

export function PaymentOverviewCard({
  invoice,
  paymentCount,
  onRecordPayment,
}: PaymentOverviewCardProps) {
  const totalCents = Number(invoice.totalCents || 0n)
  const tipCents = Number(invoice.tipCents || 0n)
  const goodwillRefundedCents = Number(invoice.goodwillRefundedCents || 0n)

  const isTipAccountingV1 = invoice.tipAccountingVersion >= 1
  const {
    paidCents,
    dueCents,
    tipPaidCents,
    tipDueCents,
  } = getCustomerFacingPaymentTotals(invoice)

  // Grand total includes tip for accurate progress calculation
  const grandTotalCents = totalCents + tipCents
  // Effective total is grand total minus goodwill refunds (which reduce what customer owes)
  const effectiveTotalCents = grandTotalCents - goodwillRefundedCents

  const progressPercentage = effectiveTotalCents > 0 ? Math.min((paidCents / effectiveTotalCents) * 100, 100) : 0
  const isPaid = effectiveTotalCents > 0 && paidCents >= effectiveTotalCents
  const hasBalance = dueCents > 0
  const isDraft = invoice.status === InvoiceStatusEnum.INVOICE_STATUS_DRAFT

  const statusDisplay = getPaymentStatusDisplay(invoice)

  return (
    <section
      aria-labelledby="invoice-payment-status-heading"
      className="space-y-5 rounded-2xl border bg-card p-5"
    >
      {/* Header with status */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 id="invoice-payment-status-heading" className="text-base font-semibold">Payment Status</h2>
        <Badge variant={statusDisplay.variant}>{statusDisplay.label}</Badge>
      </div>

      <div className="space-y-6">
        {/* Main amount display */}
        <div className="space-y-3">
          <div className="flex items-baseline gap-2">
            <span className={cn(
              'font-financial text-4xl font-semibold tabular-nums tracking-tight',
              isPaid ? 'text-status-success-fg' : ''
            )}>
              {formatCurrency(paidCents)}
            </span>
            <span className="text-lg text-muted-foreground">
              paid
            </span>
          </div>

          {isTipAccountingV1 && tipPaidCents > 0 && (
            <p className="font-financial text-xs tabular-nums text-muted-foreground">
              incl. {formatCurrency(tipPaidCents)} tip
            </p>
          )}

          {/* Progress bar */}
          <div className="relative h-3 w-full overflow-hidden rounded-full bg-muted">
            <div
              className={cn(
                'h-full rounded-full transition-all duration-500 ease-out',
                isPaid
                  ? 'bg-status-success-fg'
                  : progressPercentage > 0
                    ? 'bg-status-info-fg'
                    : 'bg-muted-foreground/20'
              )}
              style={{ width: `${progressPercentage}%` }}
            />
            {/* Shimmer effect for partial payments */}
            {!isPaid && progressPercentage > 0 && (
              <div
                className="absolute inset-0 h-full bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer"
                style={{ width: `${progressPercentage}%` }}
              />
            )}
          </div>

          <div className="flex items-center justify-between gap-4 text-sm">
            <span className="text-muted-foreground">
              of {formatCurrency(effectiveTotalCents)} total
            </span>
            <span className="font-financial font-medium tabular-nums">
              {progressPercentage.toFixed(0)}%
            </span>
          </div>

          {isTipAccountingV1 && tipDueCents > 0 && (
            <div className="flex items-center justify-between gap-4 text-xs text-muted-foreground">
              <span>Tip remaining</span>
              <span className="font-financial tabular-nums">{formatCurrency(tipDueCents)}</span>
            </div>
          )}

          {/* Breakdown when goodwill refund exists */}
          {goodwillRefundedCents > 0 && (
            <div className="space-y-0.5 border-t border-dashed pt-1 text-xs">
              <div className="flex justify-between gap-4 text-muted-foreground">
                <span>Original Total</span>
                <span className="font-financial tabular-nums">{formatCurrency(grandTotalCents)}</span>
              </div>
              <div className="flex justify-between gap-4 text-status-success-fg">
                <span>Goodwill Refund</span>
                <span className="font-financial tabular-nums">-{formatCurrency(goodwillRefundedCents)}</span>
              </div>
            </div>
          )}
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-3 gap-4">
          <StatCard
            icon={<Receipt className="size-4" aria-hidden="true" />}
            value={paymentCount.toString()}
            label={paymentCount === 1 ? 'Payment' : 'Payments'}
          />
          <StatCard
            icon={<TrendingUp className="size-4" aria-hidden="true" />}
            value={formatCurrency(dueCents)}
            label="Remaining"
            highlight={hasBalance}
          />
          <StatCard
            icon={<CreditCard className="size-4" aria-hidden="true" />}
            value={progressPercentage >= 100 ? 'Complete' : `${progressPercentage.toFixed(0)}%`}
            label="Progress"
          />
        </div>

        {/* Action button */}
        {hasBalance && !isDraft && (
          <div className="pt-2">
            <ActionGate
              organizationId={invoice.organizationId}
              permission="PaymentsManage"
              verb="record"
              noun="payments"
            >
              <Button
                onClick={onRecordPayment}
                className="w-full"
                variant="default"
              >
                <Plus className="size-4" aria-hidden="true" />
                Record Payment
              </Button>
            </ActionGate>
          </div>
        )}

        {/* Paid celebration state */}
        {isPaid && (
          <div className="flex items-center justify-center gap-2 py-2 text-status-success-fg">
            <svg
              aria-hidden="true"
              className="size-5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
            <span className="font-medium">Fully Paid</span>
          </div>
        )}
      </div>
    </section>
  )
}

interface StatCardProps {
  icon: React.ReactNode
  value: string
  label: string
  highlight?: boolean
}

function StatCard({ icon, value, label, highlight }: StatCardProps) {
  return (
    <div className={cn(
      'flex flex-col items-center justify-center rounded-xl border px-4 py-3 transition-colors',
      highlight ? 'border-status-warning-border bg-status-warning-bg' : 'bg-muted/30'
    )}>
      <div className={cn(
        'mb-1',
        highlight ? 'text-status-warning-fg' : 'text-muted-foreground'
      )}>
        {icon}
      </div>
      <div className={cn(
        'font-financial text-lg font-semibold tabular-nums',
        highlight ? 'text-status-warning-fg' : ''
      )}>
        {value}
      </div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  )
}
